import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { RouteSEO } from "@/components/SEO";
import { UpdateBanner } from "@/components/UpdateBanner";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import FeedPage from "@/pages/FeedPage";
import CreatePostPage from "@/pages/CreatePostPage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import PostDetailPage from "@/pages/PostDetailPage";
import MessagesPage from "@/pages/MessagesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import CalendarPage from "@/pages/CalendarPage";
import SearchPage from "@/pages/SearchPage";
import MapPage from "@/pages/MapPage";
import ChatPage from "@/pages/ChatPage";
import CommerceSettingsPage from "@/pages/CommerceSettingsPage";
import AdminPage from "@/pages/AdminPage";
import BoostSuccessPage from "@/pages/BoostSuccessPage";
import BoostCancelPage from "@/pages/BoostCancelPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import SubscriptionCancelPage from "@/pages/SubscriptionCancelPage";
import StatsPage from "@/pages/StatsPage";
import CommunePage from "@/pages/CommunePage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/i18n";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verifyAuth = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate("/auth", { replace: true });
      }
      setIsChecking(false);
    };

    verifyAuth();
  }, [checkAuth, navigate, location.state]);

  if (isChecking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return null;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions - process new session BEFORE checking existing auth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <RouteSEO />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/feed" element={
        <ProtectedRoute>
          <FeedPage />
        </ProtectedRoute>
      } />
      <Route path="/create" element={
        <ProtectedRoute>
          <CreatePostPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={<ProfilePage />} />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/post/:postId" element={<PostDetailPage />} />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:recipientId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={<CalendarPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/map" element={<MapPage />} />
      <Route path="/commune/:slug" element={<CommunePage />} />
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/chat/:roomId" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      <Route path="/commerce-settings" element={
        <ProtectedRoute>
          <CommerceSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/boost/success" element={
        <ProtectedRoute>
          <BoostSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/boost/cancel" element={
        <ProtectedRoute>
          <BoostCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SubscriptionPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/cancel" element={
        <ProtectedRoute>
          <SubscriptionCancelPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/stats" element={
        <ProtectedRoute>
          <StatsPage />
        </ProtectedRoute>
      } />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </>
  );
};

const NotFoundPage = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 p-6 text-center" data-testid="not-found-page">
    <h1 className="text-4xl sm:text-5xl font-bold text-primary">404</h1>
    <p className="text-muted-foreground">Cette page n'existe pas ou a été déplacée.</p>
    <a href="/" className="text-primary underline" data-testid="not-found-home-link">Retour à l'accueil</a>
  </div>
);

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App min-h-screen bg-background text-foreground">
              <AppRouter />
              <UpdateBanner />
              <Toaster richColors position="top-center" />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
